import { useDocumentsStorageApi } from 'infrastructure/api';
import { LoadingParams } from 'framework/list';
import { cloneDeep } from 'lodash';
import { DocumentStorageContextData } from 'framework/documentStorage';
import { FormFieldsData } from 'framework/form';
import { RenderPreviewDocumentDataTypes, TokenDocumentTypes } from 'framework/documentStorage';
import processAssetValidationSchema from './createupdate/processAssetValidationSchema';
import { ProcessAssetsCommitType, ProcessAssetsFormType, ProcessAssetsType } from './types';
import { useDateService } from 'framework/utils';
import { useMemo } from 'react';
import { PROCESS_ASSETS_STATUS } from './constants';
import {
  normalizeStoragePath,
  normalizeStoragePathWithSlash,
  useDocumentStorageReceiver,
} from 'domain/shared/documentStorage';

export const mapAssetDocumentForCommit = (docData: ProcessAssetsFormType): ProcessAssetsCommitType => {
  const paths = docData?.paths?.length ? docData?.paths : [];

  return {
    templateName: docData.templateName,
    owner: docData.owner,
    section: docData.section,
    fileType: docData.fileType,
    office: docData.office,
    version: docData.version,
    status: docData.status,
    paths: paths.length ? [normalizeStoragePathWithSlash(paths[0])] : [],
  };
};

const useProcessAssetsDocumentService = (): DocumentStorageContextData => {
  const { getTimeDifference, getMomentDateObj } = useDateService();
  const { sendPostRequest } = useDocumentsStorageApi('/operational-assets');

  const searchDocuments = async (requestParams: LoadingParams) => {
    try {
      const params = cloneDeep(requestParams);
      const searchString = params.filter?.['specification:search']?.eq || '';

      if (searchString && params.filter) {
        delete params.filter?.['specification:search'];
        params.filter.entityId = {
          // eq with templateName
          ct: searchString,
        };
      }

      const request = await sendPostRequest('/search', {
        ...params,
        filter: {
          ...params.filter,
        },
      });

      const response = await request.json();

      const documents = (response.result || []).map((item: ProcessAssetsType) => ({
        ...item,
        metadata: {
          ...item.metadata,
          templateName: item.metadata.entityId,
          status: item.metadata.status || PROCESS_ASSETS_STATUS.UNPUBLISHED,
        },
      })) as ProcessAssetsType[];

      const groupedByTemplateName: Record<string, ProcessAssetsType[]> = documents.reduce(
        (acc: Record<string, ProcessAssetsType[]>, doc: ProcessAssetsType) => {
          const key = doc.metadata.templateName;
          if (!acc[key]) acc[key] = [];
          acc[key].push(doc);
          return acc;
        },
        {},
      );

      const finalResult: ProcessAssetsType[] = Object.values(groupedByTemplateName)
        .map(docs => {
          const sorted = docs.slice().sort((a, b) => {
            // 1) version desc
            const vDiff = (b.metadata.version || 0) - (a.metadata.version || 0);
            if (vDiff !== 0) return vDiff;

            // 2) createdAt desc
            return getMomentDateObj(b.metadata.createdAt).diff(getMomentDateObj(a.metadata.createdAt));
          });

          const [latest, ...others] = sorted;
          return {
            ...latest,
            metadata: {
              ...latest.metadata,
              previousVersions: others,
            },
          };
        })
        // Global sort by createdAt
        .sort((a, b) => {
          const dateA = getMomentDateObj(a.metadata.createdAt);
          const dateB = getMomentDateObj(b.metadata.createdAt);
          return getTimeDifference(dateA, dateB);
        });

      return {
        ...response,
        result: finalResult,
      };
    } catch {
      return {
        result: [],
      };
    }
  };

  const getDocumentByPaths = useDocumentStorageReceiver(async (paths: string[]) => {
    const request = await sendPostRequest('/files', {
      paths,
    });
    const response = await request.json();
    return response?.result?.length
      ? response.result.map((item: ProcessAssetsType) => {
          return {
            ...item,
            metadata: {
              ...item.metadata,
              templateName: item.metadata.entityId,
            },
          };
        })
      : [];
  });

  const uploadDocument = async (data: FormFieldsData) => {
    const request = await sendPostRequest('/commit', mapAssetDocumentForCommit(data as ProcessAssetsFormType));
    return request.json();
  };

  const editDocument = async (data: FormFieldsData) => {
    const request = await sendPostRequest('/commit', mapAssetDocumentForCommit(data as ProcessAssetsFormType));
    const response = await request.json();
    return {
      ...data,
      ...response,
    }; // To update
  };

  const getDocumentToken = async (path: string) => {
    const request = await sendPostRequest('/presigned-link', {
      path: normalizeStoragePath(path),
    });
    const responce = (await request.json()) as TokenDocumentTypes;
    return responce.metadata.preSignedUrl;
  };

  const renderPreviewDocumentTemplate = async (data: RenderPreviewDocumentDataTypes) => {
    const request = await sendPostRequest('/preview-render', {
      ...data,
    });
    return request.json();
  };

  const getValidationSchema = () => Promise.resolve(processAssetValidationSchema);

  return useMemo(
    () => ({
      getDocumentByPaths,
      uploadDocument,
      editDocument,
      searchDocuments,
      getValidationSchema,
      getDocumentToken,
      renderPreviewDocumentTemplate,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
};

export default useProcessAssetsDocumentService;
